import React from 'react';
import { Link } from 'react-router-dom';
import LegalPage, { Section, Champ } from '../components/LegalPage';
import { EDITEUR, PROTECTION_DONNEES, SOUS_TRAITANTS } from '../legalInfo';

export default function Confidentialite() {
  return (
    <LegalPage
      titre="Politique de confidentialité"
      chapo="Comment Jalonne traite les données personnelles, conformément au règlement (UE) 2016/679 (RGPD) et à la loi Informatique et Libertés."
    >
      <Section titre="1. Deux rôles distincts">
        <p>
          Jalonne intervient à deux titres, qu'il est important de distinguer :
        </p>
        <ul>
          <li>
            <strong>Responsable de traitement</strong> pour les données de ses propres clients :
            comptes des utilisateurs professionnels, facturation, sécurité, relation commerciale.
            C'est l'objet de la présente politique.
          </li>
          <li>
            <strong>Sous-traitant</strong> pour les données que les entreprises clientes saisissent
            dans la plateforme et qui concernent leurs propres clients ou des tiers (abonnés
            au suivi d'un chantier, personnes éventuellement visibles sur les photos). Dans
            ce cas, l'entreprise cliente est responsable de traitement et Jalonne agit sur ses
            instructions : voir l'<Link to="/sous-traitance">accord de sous-traitance</Link>.
          </li>
        </ul>
        <p>
          Responsable de traitement :{' '}
          <Champ valeur={EDITEUR.raisonSociale} attendu="raison sociale" />,{' '}
          <Champ valeur={EDITEUR.siege} attendu="adresse du siège" />.
        </p>
      </Section>

      <Section titre="2. Données collectées et finalités">
        <div className="legal-scroll">
          <table className="legal-table">
            <thead>
              <tr>
                <th>Données</th>
                <th>Finalité</th>
                <th>Base légale</th>
                <th>Conservation</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Nom, adresse email, mot de passe (haché), rôle, entreprise de rattachement</td>
                <td>Création et gestion du compte, authentification</td>
                <td>Exécution du contrat</td>
                <td>Durée du contrat + 30 jours</td>
              </tr>
              <tr>
                <td>Adresse IP, agent utilisateur, événements d'authentification (connexion, échec, réinitialisation, activation 2FA)</td>
                <td>Sécurité des comptes, détection des accès frauduleux, limitation des tentatives</td>
                <td>Intérêt légitime (sécurité du service)</td>
                <td>12 mois</td>
              </tr>
              <tr>
                <td>Jetons de session et de rafraîchissement, codes de secours 2FA</td>
                <td>Maintien de la session, double authentification</td>
                <td>Exécution du contrat</td>
                <td>Jusqu'à expiration ou déconnexion</td>
              </tr>
              <tr>
                <td>Formule souscrite, statut d'abonnement</td>
                <td>Gestion des abonnements et de la facturation</td>
                <td>Exécution du contrat</td>
                <td>Durée du contrat</td>
              </tr>
              <tr>
                <td>Factures</td>
                <td>Obligations comptables</td>
                <td>Obligation légale</td>
                <td>10 ans</td>
              </tr>
              <tr>
                <td>Coordonnées publiques de l'entreprise (téléphone, email, adresse, site web)</td>
                <td>Affichage sur la vitrine publique du client</td>
                <td>Exécution du contrat</td>
                <td>Durée du contrat</td>
              </tr>
              <tr>
                <td>Statistiques de consultation : compteurs agrégés par jour et par origine (QR ou accès direct)</td>
                <td>Mesure d'audience des pages publiques</td>
                <td>Intérêt légitime</td>
                <td>Agrégats sans donnée personnelle, conservés sans limite</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Aucune donnée bancaire n'est collectée ni stockée par Jalonne : le service n'est pas payé
          en ligne.
        </p>
      </Section>

      <Section titre="3. Données traitées pour le compte des entreprises clientes">
        <p>
          Les données suivantes sont saisies par les entreprises clientes ou par les visiteurs de
          leurs pages publiques. Jalonne les héberge et les traite sur instruction du client :
        </p>
        <ul>
          <li>
            <strong>Suivi d'un chantier par email</strong> : adresse email de la personne qui s'y
            abonne, avec confirmation par double opt-in et lien de désabonnement dans chaque envoi ;
          </li>
          <li>
            <strong>Contenus de chantier</strong> : photographies, descriptions, commentaires
            d'avancement, coordonnées des intervenants.
          </li>
        </ul>
        <p>
          Pour toute demande relative à ces données, la personne concernée s'adresse en premier lieu
          à l'entreprise qui exploite le chantier. Jalonne relaie la demande au client concerné
          lorsqu'elle lui est adressée directement.
        </p>
      </Section>

      <Section titre="4. Destinataires et sous-traitants">
        <p>
          Les données ne sont ni vendues, ni louées, ni communiquées à des tiers à des fins
          publicitaires. Elles sont accessibles aux seules personnes habilitées de l'Éditeur et aux
          prestataires suivants :
        </p>
        <div className="legal-scroll">
          <table className="legal-table">
            <thead>
              <tr><th>Prestataire</th><th>Rôle</th><th>Données concernées</th><th>Localisation</th></tr>
            </thead>
            <tbody>
              {SOUS_TRAITANTS.map((s, i) => (
                <tr key={i}>
                  <td><Champ valeur={s.nom} attendu="nom du prestataire" /></td>
                  <td>{s.role}</td>
                  <td>{s.donnees}</td>
                  <td><Champ valeur={s.localisation} attendu="localisation des serveurs" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          Les transferts éventuels hors Union européenne sont encadrés par les clauses
          contractuelles types de la Commission européenne.
        </p>
      </Section>

      <Section titre="5. Cookies et stockage local">
        <p>
          <strong>La plateforme ne dépose aucun cookie publicitaire, ni aucun traceur de mesure
          d'audience tierce.</strong> Les statistiques de consultation reposent sur de simples
          compteurs agrégés côté serveur, sans identifiant de visiteur.
        </p>
        <p>
          Le navigateur conserve dans son stockage local (<code>localStorage</code>) le jeton
          d'authentification et le jeton de rafraîchissement des utilisateurs connectés. Ce stockage
          est strictement nécessaire à la fourniture du service expressément demandé par
          l'utilisateur : il est à ce titre dispensé de consentement préalable (article 82 de la loi
          Informatique et Libertés). La déconnexion l'efface.
        </p>
      </Section>

      <Section titre="6. Sécurité">
        <p>Les mesures techniques suivantes sont mises en œuvre :</p>
        <ul>
          <li>chiffrement des échanges en HTTPS ;</li>
          <li>mots de passe stockés sous forme de condensats non réversibles ;</li>
          <li>double authentification (2FA) disponible, activable obligatoirement par l'administrateur d'un espace ;</li>
          <li>cloisonnement strict des espaces clients, vérifié par des tests automatisés d'isolation ;</li>
          <li>limitation du nombre de tentatives de connexion et verrouillage progressif ;</li>
          <li>journalisation des événements d'authentification.</li>
        </ul>
      </Section>

      <Section titre="7. Vos droits">
        <p>
          Toute personne dispose des droits d'accès, de rectification, d'effacement, de limitation,
          d'opposition et de portabilité, ainsi que du droit de définir des directives relatives au
          sort de ses données après son décès.
        </p>
        <p>
          Ces droits s'exercent auprès de{' '}
          <Champ valeur={PROTECTION_DONNEES.contactEmail} attendu="email dédié aux demandes RGPD" />
          {PROTECTION_DONNEES.adressePostale ? `, ou par courrier : ${PROTECTION_DONNEES.adressePostale}` : ''}.
          Une réponse est apportée dans un délai d'un mois. Une pièce d'identité peut être demandée
          en cas de doute raisonnable sur l'identité du demandeur.
        </p>
        <p>
          Les administrateurs d'un espace client disposent en outre, directement depuis leurs
          réglages, d'un <strong>export complet de leurs données</strong> et d'une{' '}
          <strong>suppression définitive de leur espace</strong>.
        </p>
        <p>
          En cas de désaccord persistant, une réclamation peut être adressée à la CNIL —{' '}
          3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07 —{' '}
          <a href="https://www.cnil.fr" target="_blank" rel="noreferrer">www.cnil.fr</a>.
        </p>
      </Section>

      <Section titre="8. Violation de données">
        <p>
          En cas de violation de données à caractère personnel susceptible d'engendrer un risque
          pour les droits et libertés des personnes, l'Éditeur notifie la CNIL dans les 72 heures et
          informe sans délai injustifié les personnes concernées lorsque le risque est élevé.
        </p>
      </Section>

      <Section titre="9. Modification de la politique">
        <p>
          La présente politique peut évoluer. Toute modification substantielle est portée à la
          connaissance des clients par email. La date de dernière mise à jour figure en tête de page.
        </p>
      </Section>
    </LegalPage>
  );
}
